import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Paper, Button, Chip,
  CircularProgress, Alert, Stack, IconButton,
  ToggleButtonGroup, ToggleButton, FormControl, InputLabel, Select,
  MenuItem, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, LinearProgress, Fade, Collapse, alpha, Checkbox,
  ListItemText, OutlinedInput,
} from '@mui/material';
import {
  ArrowBack, Lock, LockOpen, TrendingUp,
  SportsCricket, CheckCircle, CompareArrows,
  EmojiEvents, Speed, Timer, KeyboardArrowRight, AutoAwesome,
  DragIndicator,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { apiService } from '../services/api';
import {
  BattingOrderOptimizationResponse,
  PerBatterStat,
  NextBatterRecommendation,
} from '../types/lineup';

const ALL_BOWL_TYPES = [
  { key: 'r_pace', label: 'R Pace', short: 'RP' },
  { key: 'l_pace', label: 'L Pace', short: 'LP' },
  { key: 'r_wristspin', label: 'R Wrist', short: 'RW' },
  { key: 'l_wristspin', label: 'L Wrist', short: 'LW' },
  { key: 'r_fingerspin', label: 'R Finger', short: 'RF' },
  { key: 'l_fingerspin', label: 'L Finger', short: 'LF' },
];

interface IncomingPlayer {
  player_id: number;
  player_name: string;
  batting_order: number;
  role: string;
  nationality: string;
}

const ROLE_COLORS: Record<string, string> = {
  batter: '#2196F3',
  bowler: '#4CAF50',
  all_rounder: '#FF9800',
  wicket_keeper: '#9C27B0',
  batting_allrounder: '#FF9800',
  bowling_allrounder: '#66BB6A',
};

const getRoleChipColor = (role: string): string =>
  ROLE_COLORS[role.toLowerCase()] ?? '#757575';

type SortableBattingRowProps = {
  player: IncomingPlayer;
  isLocked: boolean;
  moved: boolean;
  optPos?: number;
  onToggleLock: (name: string, pos: number) => void;
  onSlotChange: (playerId: number, slot: number) => void;
};

const SortableBattingOrderRow: React.FC<SortableBattingRowProps> = ({
  player: p,
  isLocked,
  moved,
  optPos,
  onToggleLock,
  onSlotChange,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: String(p.player_id) });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.45,
        px: 1.5,
        borderRadius: 1.5,
        mb: 0.25,
        bgcolor: isLocked
          ? (t) => alpha(t.palette.primary.main, 0.06)
          : 'transparent',
        border: '1px solid',
        borderColor: isLocked ? 'primary.main' : 'transparent',
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
        },
      }}
    >
      <IconButton
        size="small"
        {...attributes}
        {...listeners}
        sx={{
          p: 0.25,
          cursor: 'grab',
          color: 'text.disabled',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicator sx={{ fontSize: 16 }} />
      </IconButton>
      <FormControl size="small" sx={{ width: 56, flexShrink: 0 }}>
        <Select
          value={p.batting_order}
          onChange={(e) => onSlotChange(p.player_id, Number(e.target.value))}
          variant="outlined"
          inputProps={{ title: '' }}
          sx={{
            height: 28,
            fontSize: '0.8rem',
            fontWeight: 700,
            '& .MuiSelect-select': { py: 0.35, px: 0.9 },
          }}
        >
          {Array.from({ length: 11 }, (_, i) => (
            <MenuItem key={i + 1} value={i + 1}>
              {i + 1}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          title={p.player_name}
          sx={{ fontWeight: 600, lineHeight: 1.25, wordBreak: 'break-word' }}
        >
          {p.player_name}
        </Typography>
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            mt: 0.3,
            px: 0.7,
            py: 0.1,
            borderRadius: 0.75,
            bgcolor: alpha(getRoleChipColor(p.role), 0.12),
            color: getRoleChipColor(p.role),
            fontWeight: 700,
            fontSize: '0.6rem',
            lineHeight: 1.6,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            whiteSpace: 'nowrap',
          }}
        >
          {p.role.replace(/_/g, ' ')}
        </Box>
      </Box>
      {moved && optPos !== undefined && (
        <Chip
          label={`→ ${optPos}`}
          size="small"
          color="success"
          variant="outlined"
          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
        />
      )}
      <IconButton
        size="small"
        onClick={() => onToggleLock(p.player_name, p.batting_order)}
        sx={{
          p: 0.35,
          color: isLocked ? 'primary.main' : 'text.disabled',
        }}
      >
        {isLocked ? <Lock sx={{ fontSize: 14 }} /> : <LockOpen sx={{ fontSize: 14 }} />}
      </IconButton>
    </Box>
  );
};

const heatmapColor = (val: number, min: number, max: number): string => {
  if (max === min) return '#F5F5F5';
  const t = (val - min) / (max - min);
  if (t < 0.5) {
    const r = Math.round(239 + (255 - 239) * (t * 2));
    const g = Math.round(83 + (235 - 83) * (t * 2));
    const b = Math.round(80 + (59 - 80) * (t * 2));
    return `rgb(${r},${g},${b})`;
  }
  const nt = (t - 0.5) * 2;
  const r = Math.round(255 - (255 - 46) * nt);
  const g = Math.round(235 - (235 - 125) * nt);
  const b = Math.round(59 + (50 - 59) * nt);
  return `rgb(${r},${g},${b})`;
};

const BattingOrderOptimizer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const passedPlayers = useMemo<IncomingPlayer[]>(() => {
    const st = location.state as { players?: IncomingPlayer[] } | null | undefined;
    const list = st?.players;
    return Array.isArray(list) ? list : [];
  }, [location.state]);

  const passedSquad = useMemo<IncomingPlayer[]>(() => {
    const st = location.state as { squadPlayers?: IncomingPlayer[] } | null | undefined;
    const list = st?.squadPlayers;
    return Array.isArray(list) ? list : [];
  }, [location.state]);

  const passedNationality: string = location.state?.nationality ?? 'India';
  const passedLeague: string = location.state?.league ?? 'IPL';

  const [players, setPlayers] = useState<IncomingPlayer[]>(passedPlayers);

  /** Full squad for sidebar context (XI + bench); optimization/recommender dropdowns use XI only. */
  const rosterPlayers = useMemo(() => {
    const xiIds = new Set(players.map((p) => p.player_id));
    if (!passedSquad?.length) {
      return [...players].sort((a, b) => a.batting_order - b.batting_order);
    }
    const byId = new Map<number, IncomingPlayer>();
    for (const row of passedSquad) {
      byId.set(row.player_id, { ...row });
    }
    for (const p of passedPlayers) {
      byId.set(p.player_id, { ...p });
    }
    for (const p of players) {
      byId.set(p.player_id, { ...p });
    }
    return Array.from(byId.values()).sort((a, b) => {
      const ax = xiIds.has(a.player_id);
      const bx = xiIds.has(b.player_id);
      if (ax !== bx) return ax ? -1 : 1;
      if (ax && bx) return a.batting_order - b.batting_order;
      return a.player_name.localeCompare(b.player_name);
    });
  }, [passedPlayers, passedSquad, players]);

  const xiPlayerIds = useMemo(
    () => new Set(players.map((p) => p.player_id)),
    [players],
  );
  const benchPlayers = useMemo(
    () => rosterPlayers.filter((p) => !xiPlayerIds.has(p.player_id)),
    [rosterPlayers, xiPlayerIds],
  );

  const [lockedPositions, setLockedPositions] = useState<Record<string, number>>({});
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const [oppBowlTypes, setOppBowlTypes] = useState<string[]>(
    ALL_BOWL_TYPES.map((b) => b.key),
  );
  const [innings, setInnings] = useState<number>(1);
  const [nationality] = useState<string>(passedNationality);
  const [league] = useState<string>(passedLeague);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<BattingOrderOptimizationResponse | null>(null);

  const [recOver, setRecOver] = useState<number>(8);
  const [recStrikerId, setRecStrikerId] = useState<number | ''>('');
  const [recCandidateIds, setRecCandidateIds] = useState<number[]>([]);
  const [recLoading, setRecLoading] = useState(false);
  const [recResults, setRecResults] = useState<NextBatterRecommendation[]>([]);

  useEffect(() => {
    if (passedPlayers.length === 0) {
      setError('No players passed. Go back to Lineup Playground and select 11 players first.');
    }
  }, [passedPlayers.length]);

  const playingXI = useMemo(
    () => [...players].sort((a, b) => a.batting_order - b.batting_order),
    [players],
  );

  const commitXiOrder = useCallback((nextOrder: IncomingPlayer[]) => {
    const withOrder = nextOrder.map((pl, i) => ({
      ...pl,
      batting_order: i + 1,
    }));
    setPlayers(withOrder);
    setLockedPositions((prev) => {
      const next: Record<string, number> = {};
      for (const name of Object.keys(prev)) {
        const pl = withOrder.find((x) => x.player_name === name);
        if (pl) next[name] = pl.batting_order;
      }
      return next;
    });
    setResult(null);
    setRecResults([]);
  }, []);

  const toggleLock = useCallback((name: string, pos: number) => {
    setLockedPositions((prev) => {
      const next = { ...prev };
      if (next[name] !== undefined) delete next[name];
      else next[name] = pos;
      return next;
    });
  }, []);

  const handleXiDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const xi = [...playingXI];
      const oldIndex = xi.findIndex((p) => String(p.player_id) === String(active.id));
      const newIndex = xi.findIndex((p) => String(p.player_id) === String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(xi, oldIndex, newIndex);
      commitXiOrder(reordered);
    },
    [playingXI, commitXiOrder],
  );

  const movePlayerToSlot = useCallback(
    (playerId: number, targetSlot: number) => {
      const xi = [...playingXI];
      const fromIdx = xi.findIndex((p) => p.player_id === playerId);
      if (fromIdx < 0) return;
      const slot = Math.max(1, Math.min(11, Math.floor(targetSlot)));
      const toIdx = slot - 1;
      if (fromIdx === toIdx) return;
      const next = [...xi];
      const [removed] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, removed);
      commitXiOrder(next);
    },
    [playingXI, commitXiOrder],
  );

  /** Bench player replaces whoever is at `slot` (1–11); full row from roster so `player_id` is preserved. */
  const swapBenchIntoSlot = useCallback(
    (benchPlayerId: number, slot: number) => {
      const benchRow = rosterPlayers.find((r) => r.player_id === benchPlayerId);
      if (!benchRow) return;
      if (playingXI.some((pl) => pl.player_id === benchPlayerId)) return;
      const slotClamped = Math.max(1, Math.min(11, Math.floor(slot)));
      const toIdx = slotClamped - 1;
      const xi = [...playingXI];
      const newXi = xi.map((pl) => ({ ...pl }));
      newXi[toIdx] = { ...benchRow, batting_order: slotClamped };
      commitXiOrder(newXi);
    },
    [rosterPlayers, playingXI, commitXiOrder],
  );

  const handleOptimize = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      console.info('[BattingOrderOptimizer] optimize request league=', league, 'nationality=', nationality);
      const resp = await apiService.optimizeBattingOrder({
        players: playingXI.map((p) => ({
          player_id: p.player_id,
          player_name: p.player_name,
          batting_position: p.batting_order,
        })),
        fixed_positions: lockedPositions,
        opp_bowl_types: oppBowlTypes,
        nationality,
        innings,
        league,
      });
      console.info('[BattingOrderOptimizer] optimize response league_context=', resp.league_context);
      setResult(resp);
    } catch (e: any) {
      setError(e?.detail || e?.message || 'Optimization failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyAndGoBack = () => {
    if (!result) return;
    const optimizedPlayers = players.map((p) => ({
      ...p,
      batting_order: result.optimal_order[p.player_id] ?? p.batting_order,
    }));
    navigate('/lineup-playground', { state: { optimizedPlayers } });
  };

  const handleRecommend = async () => {
    if (!result || recStrikerId === '' || recCandidateIds.length === 0) return;
    setRecLoading(true);
    try {
      const resp = await apiService.recommendNextBatter({
        state_value_table: result.state_value_table,
        over: recOver,
        striker_id: Number(recStrikerId),
        candidate_ids: recCandidateIds,
      });
      setRecResults(resp.recommendations);
    } catch (e: any) {
      setError(e?.detail || e?.message || 'Recommendation failed');
    } finally {
      setRecLoading(false);
    }
  };

  const sortedBatterStats: PerBatterStat[] = useMemo(() => {
    if (!result) return [];
    const posMap = result.optimal_order_names;
    return [...result.per_batter_stats].sort(
      (a, b) => (posMap[a.striker_name] ?? 99) - (posMap[b.striker_name] ?? 99),
    );
  }, [result]);

  /** SR integers from rounded Runs/Balls (matches displayed columns). */
  const recommendationRows = useMemo(() => {
    const rows = recResults.map((r) => {
      const dispRuns = Math.round(Number(r.expected_runs));
      const dispBalls = Math.round(Number(r.expected_balls));
      const displaySr =
        dispBalls > 0 ? Math.round((dispRuns / dispBalls) * 100) : 0;
      return { rec: r, dispRuns, dispBalls, displaySr };
    });
    const maxSr =
      rows.length === 0 ? -1 : Math.max(...rows.map((row) => row.displaySr));
    const bestSrRankable = maxSr > 0;
    return rows.map((row) => ({
      ...row,
      isBestSr: bestSrRankable && row.displaySr === maxSr,
    }));
  }, [recResults]);

  const improvement = result ? result.optimal_runs - result.current_runs : 0;
  const improvementPct = result && result.current_runs > 0
    ? ((improvement / result.current_runs) * 100).toFixed(1)
    : '0';

  if (players.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <SportsCricket sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            No Lineup Data
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Go back to Lineup Playground and build a Playing XI first.
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/lineup-playground')}
          >
            Back to Playground
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8F9FA' }}>
      {/* Top header bar */}
      <Paper
        elevation={0}
        sx={{
          px: 3, py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'white',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, maxWidth: 1400, mx: 'auto' }}>
          <IconButton onClick={() => navigate('/lineup-playground')} size="small">
            <ArrowBack />
          </IconButton>
          <SportsCricket sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
            Batting Order Optimizer
          </Typography>
          <Chip
            label={league}
            size="small"
            color="primary"
            variant="outlined"
            title="League used for LOS + bowling deployments"
          />
          <Chip
            label={`${innings === 1 ? '1st' : '2nd'} Innings`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`${Object.keys(lockedPositions).length} locked`}
            size="small"
            icon={<Lock sx={{ fontSize: '14px !important' }} />}
            variant="outlined"
            color={Object.keys(lockedPositions).length > 0 ? 'primary' : 'default'}
          />
        </Box>
      </Paper>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* ===== LEFT SIDEBAR ===== */}
          <Box sx={{ width: 300, flexShrink: 0 }}>
            {/* Lineup with locks */}
            <Paper
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                mb: 2,
                height: 'calc(100vh - 165px)',
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  minHeight: 56,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (t) => alpha(t.palette.secondary.main, 0.05),
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, textAlign: 'center', letterSpacing: 0.3, color: 'secondary.main' }}
                >
                  Current Batting Order
                </Typography>
              </Box>
              <Box sx={{ p: 0.75, height: 'calc(100% - 56px)' }}>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleXiDragEnd}
                >
                  <SortableContext
                    items={playingXI.map((p) => String(p.player_id))}
                    strategy={verticalListSortingStrategy}
                  >
                    {playingXI.map((p) => {
                      const isLocked = lockedPositions[p.player_name] !== undefined;
                      const optPos = result?.optimal_order_names?.[p.player_name];
                      const moved = optPos !== undefined && optPos !== p.batting_order;
                      return (
                        <SortableBattingOrderRow
                          key={p.player_id}
                          player={p}
                          isLocked={isLocked}
                          moved={moved}
                          optPos={optPos}
                          onToggleLock={toggleLock}
                          onSlotChange={movePlayerToSlot}
                        />
                      );
                    })}
                  </SortableContext>
                </DndContext>
              </Box>
            </Paper>
          </Box>

          {/* ===== SQUAD COLUMN ===== */}
          <Box sx={{ width: 270, flexShrink: 0 }}>
            <Paper
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                height: 'calc(100vh - 165px)',
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  minHeight: 56,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: (t) => alpha(t.palette.secondary.main, 0.05),
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, textAlign: 'center', letterSpacing: 0.3, color: 'secondary.main' }}
                >
                  Squad (not in current XI) — {benchPlayers.length}
                </Typography>
              </Box>
              <Box sx={{ p: 1, overflowY: 'auto', height: 'calc(100% - 56px)' }}>
                {benchPlayers.map((p) => (
                  <Box
                    key={p.player_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      py: 0.5,
                      px: 1,
                      borderRadius: 1,
                      opacity: 0.95,
                      minWidth: 0,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" title={p.player_name} sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
                        {p.player_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }} noWrap>
                        {p.role.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                    <FormControl size="small" sx={{ flexShrink: 0, minWidth: 88 }}>
                      <Select
                        value=""
                        displayEmpty
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') return;
                          const slot = Number(raw);
                          if (slot >= 1 && slot <= 11) {
                            swapBenchIntoSlot(p.player_id, slot);
                          }
                        }}
                        renderValue={() => 'Swap'}
                        sx={{ fontSize: '0.7rem', '& .MuiSelect-select': { py: 0.45, px: 0.9 } }}
                      >
                        <MenuItem value="" disabled sx={{ fontSize: '0.75rem' }}>
                          <em>Slot 1–11</em>
                        </MenuItem>
                        {Array.from({ length: 11 }, (_, i) => (
                          <MenuItem key={i + 1} value={i + 1} sx={{ fontSize: '0.75rem' }}>
                            {i + 1} - {playingXI[i]?.player_name ?? 'Empty'}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>

          {/* ===== RIGHT CONTENT ===== */}
          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
            {/* Simulation settings (moved above summary/results) */}
            <Paper
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
                mb: 2,
              }}
            >
              <Box
                sx={{
                  px: 2.5,
                  py: 1.5,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: (t) => alpha(t.palette.secondary.main, 0.05),
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, textAlign: 'center', letterSpacing: 0.3, color: 'secondary.main' }}
                >
                  Simulation Settings
                </Typography>
              </Box>
              <Box sx={{ p: 2.5 }}>
                {/* Innings */}
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}>
                  INNINGS
                </Typography>
                <ToggleButtonGroup
                  value={innings}
                  exclusive
                  onChange={(_, v) => v !== null && setInnings(v)}
                  size="small"
                  fullWidth
                  sx={{
                    mb: 1.5,
                    '& .MuiToggleButton-root': {
                      py: 0.75,
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                    },
                  }}
                >
                  <ToggleButton value={1}>
                    1st Innings
                  </ToggleButton>
                  <ToggleButton value={2}>
                    2nd Innings
                  </ToggleButton>
                </ToggleButtonGroup>

                {/* Bowl types */}
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.75, display: 'block' }}>
                  OPPOSITION BOWLING TYPES
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    mb: 1.25,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      // Take remaining space so the optimise action can sit clearly on the right
                      flex: 1,
                      minWidth: 0,
                      overflowX: 'auto',
                      py: 0.25,
                      pr: 0.25,
                      // keep chips + optimize on one visual row; scroll chips if needed
                      flexWrap: 'nowrap',
                      scrollbarWidth: 'thin',
                    }}
                  >
                    {ALL_BOWL_TYPES.map((bt) => {
                      const selected = oppBowlTypes.includes(bt.key);
                      return (
                        <Chip
                          key={bt.key}
                          label={bt.label}
                          size="small"
                          color={selected ? 'primary' : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          onClick={() =>
                            setOppBowlTypes((prev) =>
                              selected ? prev.filter((x) => x !== bt.key) : [...prev, bt.key],
                            )
                          }
                          sx={{
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: '0.72rem',
                            height: 28,
                            px: 0.25,
                            flex: '0 0 auto',
                          }}
                        />
                      );
                    })}
                  </Box>

                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleOptimize}
                    disabled={loading || oppBowlTypes.length === 0}
                    startIcon={
                      loading ? (
                        <CircularProgress size={16} sx={{ color: 'white' }} />
                      ) : (
                        <AutoAwesome sx={{ fontSize: 18 }} />
                      )
                    }
                    sx={{
                      flex: '0 0 auto',
                      // Nudge in from the far-right edge of the card
                      mr: 1,
                      height: 38,
                      minHeight: 38,
                      minWidth: 280,
                      px: 2.75,
                      borderRadius: 999,
                      fontWeight: 700,
                      fontSize: '0.82rem',
                      letterSpacing: 0.2,
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      boxShadow: (t) => `0 6px 16px ${alpha(t.palette.primary.main, 0.26)}`,
                      '&:hover': {
                        boxShadow: (t) => `0 8px 20px ${alpha(t.palette.primary.main, 0.34)}`,
                      },
                      '& .MuiButton-startIcon': { ml: 0, mr: 0.5 },
                    }}
                  >
                    {loading ? 'Running…' : 'Optimise Batting Order'}
                  </Button>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                  1,500 simulations per run
                </Typography>

                {error && (
                  <Alert
                    severity="error"
                    onClose={() => setError('')}
                    sx={{ mt: 2, borderRadius: 2 }}
                  >
                    {error}
                  </Alert>
                )}
              </Box>
            </Paper>

            {/* Loading state */}
            {loading && (
              <Fade in>
                <Paper
                  elevation={0}
                  sx={{
                    p: 6,
                    textAlign: 'center',
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ position: 'relative', display: 'inline-flex', mb: 3 }}>
                    <CircularProgress size={80} thickness={2} />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <SportsCricket sx={{ fontSize: 32, color: 'primary.main' }} />
                    </Box>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Running Innings Simulations
                  </Typography>
                  <Typography color="text.secondary" sx={{ mb: 3 }}>
                    Optimizing batting positions...
                  </Typography>
                  <LinearProgress
                    sx={{
                      maxWidth: 400,
                      mx: 'auto',
                      borderRadius: 2,
                      height: 6,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                    This typically takes 15-30 seconds
                  </Typography>
                </Paper>
              </Fade>
            )}

            {/* Empty state */}
            {!loading && !result && (
              <Paper
                elevation={0}
                sx={{
                  p: 8,
                  textAlign: 'center',
                  borderRadius: 3,
                  border: '2px dashed',
                  borderColor: 'divider',
                }}
              >
                <TrendingUp sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Ready to Optimize
                </Typography>
                <Typography color="text.disabled" sx={{ mt: 0.5 }}>
                  Configure settings and click Optimize to find the best batting order
                </Typography>
              </Paper>
            )}

            {/* ===== RESULTS ===== */}
            {result && !loading && (
              <Fade in>
                <Stack spacing={2.5}>
                  {result.league_context && (
                    <Alert
                      severity="info"
                      variant="outlined"
                      sx={{
                        py: 0.25,
                        borderRadius: 2,
                        color: 'text.secondary',
                        bgcolor: (t) => alpha(t.palette.secondary.main, 0.04),
                        borderColor: (t) => alpha(t.palette.secondary.main, 0.18),
                        '& .MuiAlert-icon': { color: 'secondary.main' },
                        '& strong': { color: 'text.primary' },
                      }}
                    >
                      League data: <strong>{result.league_context.league}</strong>
                      {' → '}
                      comp_name <strong>{result.league_context.comp_name}</strong>
                      {' · '}
                      source <strong>{result.league_context.data_source}</strong>
                      {' · '}
                      {result.league_context.sim_innings_count} simulated innings
                    </Alert>
                  )}
                  {/* ── Summary Cards ── */}
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {/* Current */}
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1,
                        p: 2.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 1 }}>
                        Current Order
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 800, mt: 0.5, color: 'text.primary' }}>
                        {result.current_runs.toFixed(1)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">expected runs</Typography>
                    </Paper>

                    {/* Arrow */}
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
                      <KeyboardArrowRight sx={{ fontSize: 32, color: 'text.disabled' }} />
                    </Box>

                    {/* Optimized */}
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 1,
                        p: 2.5,
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: 'success.main',
                        textAlign: 'center',
                        bgcolor: (t) => alpha(t.palette.success.main, 0.04),
                      }}
                    >
                      <Typography variant="overline" color="success.main" sx={{ fontWeight: 600, letterSpacing: 1 }}>
                        Optimized Order
                      </Typography>
                      <Typography variant="h3" sx={{ fontWeight: 800, mt: 0.5, color: 'success.dark' }}>
                        {result.optimal_runs.toFixed(1)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">expected runs</Typography>
                    </Paper>

                    {/* Improvement */}
                    <Paper
                      elevation={0}
                      sx={{
                        flex: 0.7,
                        p: 2.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: improvement >= 0 ? 'success.light' : 'error.light',
                        textAlign: 'center',
                        bgcolor: improvement >= 0
                          ? (t) => alpha(t.palette.success.main, 0.06)
                          : (t) => alpha(t.palette.error.main, 0.06),
                      }}
                    >
                      <Typography
                        variant="overline"
                        sx={{ fontWeight: 600, letterSpacing: 1, color: improvement >= 0 ? 'success.main' : 'error.main' }}
                      >
                        Improvement
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: 800, mt: 0.5, color: improvement >= 0 ? 'success.dark' : 'error.dark' }}
                      >
                        {improvement >= 0 ? '+' : ''}{improvement.toFixed(1)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {improvement >= 0 ? '+' : ''}{improvementPct}%
                      </Typography>
                    </Paper>
                  </Box>

                  {/* ── Optimized Batting Order ── */}
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        px: 2.5,
                        py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        bgcolor: '#FAFAFA',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <EmojiEvents sx={{ color: '#FFC107', fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center' }}>
                          Optimized Batting Order
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircle />}
                        onClick={handleApplyAndGoBack}
                        size="small"
                        sx={{
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: 1.5,
                          boxShadow: 'none',
                          position: 'absolute',
                          right: 16,
                        }}
                      >
                        Apply & Go Back
                      </Button>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {Object.entries(result.optimal_order_names)
                          .sort(([, a], [, b]) => a - b)
                          .map(([name, pos]) => {
                            const origPos = playingXI.find((p) => p.player_name === name)?.batting_order;
                            const changed = origPos !== undefined && origPos !== pos;
                            const isLocked = lockedPositions[name] !== undefined;
                            return (
                              <Paper
                                key={name}
                                variant="outlined"
                                sx={{
                                  px: 1.5,
                                  py: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                  minWidth: 180,
                                  borderRadius: 2,
                                  borderColor: changed ? 'success.main' : 'divider',
                                  bgcolor: changed
                                    ? (t) => alpha(t.palette.success.main, 0.04)
                                    : 'transparent',
                                  transition: 'all 0.2s',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: changed ? 'success.main' : 'primary.main',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    flexShrink: 0,
                                  }}
                                >
                                  {pos}
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                    {name}
                                  </Typography>
                                  {changed && origPos && (
                                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 500, fontSize: '0.65rem' }}>
                                      was #{origPos}
                                    </Typography>
                                  )}
                                </Box>
                                {isLocked && <Lock sx={{ fontSize: 14, color: 'primary.main' }} />}
                              </Paper>
                            );
                          })}
                      </Box>
                    </Box>
                  </Paper>

                  {/* ── Per-Batter Stats ── */}
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        px: 2.5,
                        py: 1.5,
                        bgcolor: '#FAFAFA',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <Speed sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center' }}>
                        Per-Batter Simulation Stats
                      </Typography>
                    </Box>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#FAFAFA' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: 'text.secondary', py: 1 }}>POS</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', color: 'text.secondary', py: 1 }}>BATTER</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', color: 'text.secondary', py: 1 }}>AVG BALLS</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', color: 'text.secondary', py: 1 }}>AVG RUNS</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', color: 'text.secondary', py: 1 }}>Strike Rate</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedBatterStats.map((s, idx) => {
                            const pos = result.optimal_order_names[s.striker_name] ?? '-';
                            // Same rounding as AVG BALLS / AVG RUNS columns so SR matches what you see (not MED SR,
                            // and not backend strike_rate which uses finer-grained floats before rounding).
                            const dispBalls = Math.round(Number(s.avg_balls));
                            const dispRuns = Math.round(Number(s.avg_runs));
                            const strikeRate =
                              Number.isFinite(dispBalls) && dispBalls > 0
                                ? (dispRuns / dispBalls) * 100
                                : NaN;
                            return (
                              <TableRow
                                key={s.striker_name}
                                sx={{
                                  '&:hover': { bgcolor: 'action.hover' },
                                  bgcolor: idx % 2 === 0 ? 'transparent' : '#FAFAFA',
                                }}
                              >
                                <TableCell>
                                  <Box
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      bgcolor: 'primary.main',
                                      color: 'white',
                                      fontWeight: 700,
                                      fontSize: '0.7rem',
                                    }}
                                  >
                                    {pos}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{s.striker_name}</TableCell>
                                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{dispBalls}</TableCell>
                                <TableCell align="right" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 600 }}>{dispRuns}</TableCell>
                                <TableCell align="right">
                                  {Number.isFinite(strikeRate) ? (
                                    <Chip
                                      label={strikeRate.toFixed(0)}
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontWeight: 700,
                                        fontSize: '0.7rem',
                                        bgcolor: strikeRate >= 150 ? '#E8F5E9' : strikeRate >= 130 ? '#FFF8E1' : '#FFEBEE',
                                        color: strikeRate >= 150 ? '#2E7D32' : strikeRate >= 130 ? '#F57F17' : '#C62828',
                                      }}
                                    />
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  {/* ── Expected Runs Matrix (Heatmap) ── */}
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        px: 2.5,
                        py: 1.5,
                        bgcolor: '#FAFAFA',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                        <CompareArrows sx={{ fontSize: 20, color: 'primary.main' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'center' }}>
                          Expected Runs Matrix
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'absolute', right: 16 }}>
                        <Box sx={{ width: 60, height: 8, borderRadius: 1, background: 'linear-gradient(90deg, #EF5350, #FFEB3B, #2E7D32)' }} />
                        <Typography variant="caption" color="text.secondary">Low → High</Typography>
                      </Box>
                    </Box>
                    <TableContainer sx={{ maxHeight: 500 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                bgcolor: 'white',
                                fontSize: '0.7rem',
                                color: 'text.secondary',
                                borderBottom: '2px solid',
                                borderColor: 'divider',
                                position: 'sticky',
                                left: 0,
                                zIndex: 3,
                              }}
                            >
                              PLAYER
                            </TableCell>
                            {Array.from({ length: 11 }, (_, i) => (
                              <TableCell
                                key={i + 1}
                                align="center"
                                sx={{
                                  fontWeight: 700,
                                  bgcolor: 'white',
                                  fontSize: '0.75rem',
                                  minWidth: 52,
                                  borderBottom: '2px solid',
                                  borderColor: 'divider',
                                }}
                              >
                                #{i + 1}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(() => {
                            const matrix = result.expected_runs_matrix;
                            const allVals = Object.values(matrix).flatMap((row) =>
                              Object.values(row).map((v) => Math.round(Number(v))),
                            );
                            const minVal = Math.min(...allVals);
                            const maxVal = Math.max(...allVals);
                            return Object.entries(matrix)
                              .sort(
                                ([a], [b]) =>
                                  (result.optimal_order_names[a] ?? 99) -
                                  (result.optimal_order_names[b] ?? 99),
                              )
                              .map(([player, posMap]) => {
                                const optPos = result.optimal_order_names[player];
                                return (
                                  <TableRow key={player} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                                    <TableCell
                                      sx={{
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                        fontSize: '0.8rem',
                                        position: 'sticky',
                                        left: 0,
                                        bgcolor: 'white',
                                        zIndex: 1,
                                        borderRight: '1px solid',
                                        borderColor: 'divider',
                                      }}
                                    >
                                      {player}
                                    </TableCell>
                                    {Array.from({ length: 11 }, (_, i) => {
                                      const pos = i + 1;
                                      const rounded = Math.round(Number(posMap[pos] ?? 0));
                                      const isOpt = optPos === pos;
                                      return (
                                        <TableCell
                                          key={pos}
                                          align="center"
                                          sx={{
                                            bgcolor: heatmapColor(rounded, minVal, maxVal),
                                            color: isOpt ? 'white' : 'text.primary',
                                            fontWeight: isOpt ? 800 : 500,
                                            fontSize: '0.75rem',
                                            fontFamily: 'monospace',
                                            position: 'relative',
                                            p: 0.75,
                                            ...(isOpt && {
                                              bgcolor: 'primary.main',
                                              color: 'white',
                                              boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.5)',
                                            }),
                                          }}
                                        >
                                          {rounded}
                                        </TableCell>
                                      );
                                    })}
                                  </TableRow>
                                );
                              });
                          })()}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>

                  {/* ── Who Should Bat Next ── */}
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        px: 2.5,
                        py: 1.5,
                        bgcolor: '#FAFAFA',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <Timer sx={{ fontSize: 20, color: 'primary.main' }} />
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2, textAlign: 'center' }}>
                          Who Should Bat Next?
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                          Pick a game situation to see the best candidate
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ p: 2.5 }}>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                        <FormControl size="small" sx={{ minWidth: 90 }}>
                          <InputLabel>Over</InputLabel>
                          <Select
                            value={recOver}
                            label="Over"
                            onChange={(e) => setRecOver(Number(e.target.value))}
                          >
                            {Array.from({ length: 20 }, (_, i) => (
                              <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel>Striker on Crease</InputLabel>
                          <Select
                            value={recStrikerId}
                            label="Striker on Crease"
                            onChange={(e) => {
                              const nextId = e.target.value as number;
                              setRecStrikerId(nextId);
                              setRecCandidateIds((prev) => prev.filter((cid) => cid !== nextId));
                            }}
                          >
                            {playingXI.map((p) => (
                              <MenuItem key={p.player_id} value={p.player_id}>
                                {p.player_name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: 280 }}>
                          <InputLabel>Candidate Batters</InputLabel>
                          <Select<number[]>
                            multiple
                            value={recCandidateIds}
                            label="Candidate Batters"
                            onChange={(e) => setRecCandidateIds(e.target.value as number[])}
                            input={<OutlinedInput label="Candidate Batters" />}
                            renderValue={(selected) =>
                              `${(selected as number[]).length} selected`
                            }
                          >
                            {playingXI
                              .filter((p) => p.player_id !== recStrikerId)
                              .map((p) => (
                                <MenuItem key={p.player_id} value={p.player_id}>
                                  <Checkbox
                                    checked={recCandidateIds.includes(p.player_id)}
                                    size="small"
                                  />
                                  <ListItemText primary={p.player_name} />
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>

                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleRecommend}
                          disabled={recLoading || recStrikerId === '' || recCandidateIds.length === 0}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 1.5,
                            px: 3,
                            boxShadow: 'none',
                          }}
                        >
                          {recLoading ? (
                            <CircularProgress size={18} sx={{ color: 'white' }} />
                          ) : (
                            'Recommend'
                          )}
                        </Button>
                      </Box>

                      <Collapse in={recResults.length > 0}>
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                          {recommendationRows.map(({ rec: r, dispRuns, dispBalls, displaySr, isBestSr }) => (
                            <Paper
                              key={r.batter}
                              variant="outlined"
                              sx={{
                                p: 2,
                                flex: '1 1 200px',
                                maxWidth: 260,
                                borderRadius: 2,
                                borderColor: isBestSr ? 'success.main' : 'divider',
                                borderWidth: isBestSr ? 2 : 1,
                                bgcolor: isBestSr
                                  ? (t) => alpha(t.palette.success.main, 0.04)
                                  : 'transparent',
                                position: 'relative',
                                overflow: 'hidden',
                              }}
                            >
                              {isBestSr && (
                                <Chip
                                  label="Best SR"
                                  size="small"
                                  color="success"
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    fontWeight: 700,
                                    fontSize: '0.6rem',
                                    height: 20,
                                  }}
                                />
                              )}
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                                {r.batter}
                              </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Runs</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{dispRuns}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Balls</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{dispBalls}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">SR</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {dispBalls > 0 ? String(displaySr) : '—'}
                                  </Typography>
                                </Box>
                              </Box>
                            </Paper>
                          ))}
                        </Box>
                      </Collapse>
                    </Box>
                  </Paper>
                </Stack>
              </Fade>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default BattingOrderOptimizer;
